import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Gamepad2, LayoutGrid, Maximize, Pencil, QrCode as QrCodeIcon, Radio, ZoomIn, ZoomOut } from 'lucide-react';
import { colors, resourceStatusColors, tableStateColors } from '@/lib/ui-tokens';
import { isKnownTableState, tableMayHaveSession } from '@/lib/domain';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useConfirm } from '@/components/ui/confirm';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useCurrency } from '@/Hooks/useCurrency';
import { useRoutedTabs } from '@/Pages/routedTabs';
import { FLOOR_MAP_MODES, type FloorMapMode } from '@/Pages/tabRoutes';
import {
  liveStates,
  useSpacesFloorMapQuery,
  useSpacesTablesQuery,
  type TableStructure,
} from '@/Redux/Slices/spacesApi';
import { useSpacesRealtime } from '@/Redux/Slices/spacesRealtime';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { MapCanvas, type View } from './components/MapCanvas';
import { FloorMapEditor } from './components/FloorMapEditor';
import { storedFloorMapView, storeFloorMapView, type FloorMapView } from './lib/floorMapView';
// Runtime, not type-only — unlike `./lib/furniture` below, `./lib/webgl` never
// touches `three`. It is one `canvas.getContext` probe, so importing it here
// (rather than only inside the lazy `FloorMap3D` chunk) costs nothing and is
// what lets the *default* be decided before that chunk is ever requested.
import { supportsWebgl } from './lib/webgl';
import { useReducedMotion } from './lib/useReducedMotion';
import type { LiveTable } from './lib/live-table';
import { TableContextMenu, type MenuAnchor, type TableAction } from './components/TableContextMenu';
import { QuickOrderDialog } from './components/QuickOrderDialog';
import { TableSessionDialog } from './components/TableSessionDialog';
import { MoveSessionDialog, type MoveTarget } from './components/MoveSessionDialog';
import { ResourceContextMenu } from './components/ResourceContextMenu';
import { ResourceBoard } from './components/ResourceBoard';
import { ResourceFormDialog } from './components/ResourceFormDialog';
import { pricingListRoute } from '@/Pages/Pricing/pricingRoutes';
import {
  ResourceDialogs,
  resourceDialogFor,
  type ResourceAction,
  type ResourceDialogState,
} from './components/ResourceDialogs';
import { liveResourceStatus, useResourceBoard } from './lib/useResourceBoard';
import type { ResourceRow, SessionRow } from './lib/sessions';
import TableQrSheet from './TableQrSheet';
import { apiMessage, statusOf } from './lib/helpers';
import { deleteResourceOutcome } from './lib/resourceDelete';
import {
  elementFill,
  type FloorScene,
  type PlaceableEntity,
  sceneFromFloor,
  type SceneElement,
} from './lib/scene';
// Type-only — `./lib/furniture` also pulls in `three` at module scope (its
// shared geometries/materials, built once), and this screen is NOT the lazy
// `FloorMap3D` chunk. A runtime import here — even of one small constant —
// would drag `three` into the bundle every session loads, defeating the
// whole point of lazying the 3D view in the first place.
import type { ElementShapeInfo } from './lib/furniture';


const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/**
 * Lazy — `three` is real weight (a few hundred kB minified). 3D is now the
 * default landing view on a device that can render it, so most sessions
 * *do* pay for this chunk on first paint — that is the trade the default
 * makes deliberately. What this split still buys: a device with no WebGL
 * (which lands on 2D per the probe above) never requests this chunk at
 * all, and someone who has explicitly chosen 2D on this device does not
 * either. Splitting keeps `three` out of the *main* bundle regardless of
 * which of those groups a session falls into.
 */
const FloorMap3D = lazy(() =>
  import('./components/FloorMap3D').then((module) => ({ default: module.FloorMap3D })),
);

/**
 * Stable query arguments — a new object literal each render would be a new
 * cache key on every render. Every key here is declared on `QueryTablesDto`;
 * `forbidNonWhitelisted` turns anything else into a 400.
 */
const TABLES_QUERY = { limit: 200, sortBy: 'number', sortOrder: 'asc' } as const;

/** A table whose state is not currently confirmed. Never a colour, never an action. */
const UNKNOWN_STATE = 'UNKNOWN';

const NO_TABLES: TableStructure[] = [];

/**
 * ADR-005 — the floor map is vector scene data, not an uploaded image, so one
 * document serves the editor, the live operational view and (later) heatmaps.
 * Both modes below render through the same `MapCanvas`.
 */
export default function FloorMap() {
  const { t } = useTranslation();
  const { can } = usePermission();
  // I-13 — only the fallback. Each resource names its own branch-resolved
  // currency, and one floor can carry resources from two branches.
  const { code: currencyCode } = useCurrency();
  // Settings links straight here — "6 of 18 on the map" is only useful if it
  // hands you to the floor and the mode that fixes it.
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const wantedFloorId = params.get('floor');
  /**
   * The mode is the URL — `/floor-map/edit` rather than `?mode=edit`. The floor
   * stays a query parameter because it is a *selection within* a mode, not a
   * different view of the screen, and it is carried across a mode change.
   */
  const routed = useRoutedTabs('/floor-map', FLOOR_MAP_MODES);
  const mode = routed.open ? routed.current : null;
  const setMode = routed.select;
  const [activeFloorId, setActiveFloorId] = useState<string | null>(wantedFloorId);
  const [floors, setFloors] = useState<FloorScene[]>([]);
  /**
   * The live board's 2D/3D preference. A rendering choice for one person on
   * one device, not a place — see `lib/floorMapView.ts` for why it lives in
   * `localStorage` rather than the URL the way the live/edit mode does.
   *
   * Precedence, in order:
   *   1. A stored preference always wins — someone who chose 2D on this
   *      device keeps getting 2D, even now that the *default* has changed.
   *   2. Absent a stored preference, 3D is the new default — but only when
   *      `supportsWebgl()` says this device can actually stand up a WebGL
   *      context. The probe runs before the choice is made, not after
   *      something fails to render, so a device without WebGL never sees
   *      the 3D pane's "not available" fallback on first open — it silently
   *      lands on 2D as if that had always been the default for it.
   */
  const [viewMode, setViewMode] = useState<FloorMapView>(
    () => storedFloorMapView() ?? (supportsWebgl() ? '3d' : '2d'),
  );
  const reducedMotion = useReducedMotion();
  const [resourceStates, setResourceStates] = useState<Record<string, string>>({});
  const [selection, setSelection] = useState<string[]>([]);
  const [view, setView] = useState<View>({ x: 0, y: 0, zoom: 1 });
  /**
   * Tap target: what is under the pointer, and where to draw its menu.
   *
   * A resource is held by id rather than by row, so the menu keeps describing
   * the resource as it is now rather than as it was when it was opened.
   */
  const [menu, setMenu] = useState<
    | { kind: 'table'; table: LiveTable; anchor: MenuAnchor }
    | { kind: 'resource'; resourceId: string; anchor: MenuAnchor }
    | null
  >(null);
  /** The table whose product menu is open, once the operator asked for one. */
  const [ordering, setOrdering] = useState<LiveTable | null>(null);
  /** The table whose bill is open. The checks screen is not a destination. */
  const [settling, setSettling] = useState<LiveTable | null>(null);
  /** The table whose session detail is open. */
  const [viewing, setViewing] = useState<LiveTable | null>(null);
  /** The table whose visit is being moved elsewhere. */
  const [moving, setMoving] = useState<LiveTable | null>(null);
  /** §10 — the resource panel: one resource, or the whole floor of them. */
  const [board, setBoard] = useState<{ open: boolean; focusId: string | null }>({
    open: false,
    focusId: null,
  });
  /** Whichever resource dialog the operator asked for (start, extend, stop…). */
  const { confirm: askConfirm } = useConfirm();
  const [resourceDialog, setResourceDialog] = useState<ResourceDialogState>(null);
  const [resourceForm, setResourceForm] = useState<{ open: boolean; record: ResourceRow | null }>({
    open: false,
    record: null,
  });

  /**
   * Structure is cached hard; state is not (see `spacesApi`). The scene document
   * and the table structure come out of the cache instantly on a revisit, while
   * `refetchOnMountOrArgChange` reconfirms the live states behind them — so the
   * map draws with no flash but never shows a colour it has not just checked.
   */
  const scenes = useSpacesFloorMapQuery();
  const tables = useSpacesTablesQuery(TABLES_QUERY, { refetchOnMountOrArgChange: true });
  /**
   * §10 — the timed resources and their live meters. This is the whole of what
   * the separate `/resources` page used to hold: a PS5 station, a billiards
   * table and a kids area are places where people sit and consume, so they are
   * worked from the floor they are drawn on, not from a page elsewhere.
   */
  const resources = useResourceBoard(can('resources.view'));

  const tableRows = tables.data?.tables ?? NO_TABLES;
  /** Stable across renders, unlike the query result object it comes from. */
  const reloadTables = tables.refetch;
  /**
   * `{}` — not the last known colours — whenever the socket has dropped or
   * nothing has been listening since the last fetch. An operator seating a table
   * someone else already took is the one failure this screen must not cause, so
   * an unconfirmed table is drawn as unknown until the refetch lands.
   */
  const tableStates = liveStates(tables.data);
  const unconfirmed = !tables.data || tables.data.unverified;
  const resourceRows = resources.resources;

  useEffect(() => {
    const list = Array.isArray(scenes.data) ? scenes.data : [];
    setFloors(list);
    setActiveFloorId((current) => {
      const asked = wantedFloorId && list.some((floor) => floor.id === wantedFloorId);
      if (asked) return wantedFloorId;
      return current && list.some((floor) => floor.id === current)
        ? current
        : (list[0]?.id ?? null);
    });
  }, [scenes.data, wantedFloorId]);

  // Resources still carry their state on the row they arrive with; tables no
  // longer do — that half now lives in the cache, where every screen sees it.
  useEffect(() => {
    setResourceStates((current) => {
      const next = { ...current };
      for (const resource of resourceRows) next[resource.id] ??= resource.status;
      return next;
    });
  }, [resourceRows]);

  /**
   * §27 — one subscription, writing into the cache rather than into this
   * component. `table.status_changed` patches every cached tables entry from the
   * event payload, so the repaint costs no round trip, and any other screen
   * showing that table moves with this one.
   */
  const live = useSpacesRealtime({
    onResourceStatus: useCallback((resourceId: string, status: string) => {
      setResourceStates((current) => ({ ...current, [resourceId]: status }));
    }, []),
  });

  const floor = useMemo(
    () => floors.find((candidate) => candidate.id === activeFloorId) ?? null,
    [floors, activeFloorId],
  );
  const scene = useMemo(() => (floor ? sceneFromFloor(floor) : null), [floor]);

  /**
   * The 3D view's guest figures (§ floor-map-3d) need a guest count per
   * table, floor-wide — `tableStates` only carries a table's *status*, never
   * how many people opened its session. `guestCount` is written exactly
   * once, at `POST /sessions` (see `mutations.ts`'s `openSession`), and
   * never again, so it changes precisely when a table's status does —
   * refetching alongside `tableStates` is therefore exact, not a guess at
   * when the number might be stale. Gated the same way `actingSessions`
   * below is: without `sessions.view` this is simply empty, and the 3D view
   * degrades to showing occupancy colour with no figures rather than
   * inventing a headcount.
   */
  const canReadSessions = can('sessions.view');
  const openSessions = useApi<unknown>(
    canReadSessions ? '/sessions' : null,
    { status: 'OPEN', limit: 200, ...(floor?.branchId ? { branchId: floor.branchId } : {}) },
  );
  const reloadOpenSessions = openSessions.reload;
  useEffect(() => {
    reloadOpenSessions();
    // `tableStates` is the RTK cache's own `states` map — a new reference
    // exactly when a socket event or refetch changed some table's status,
    // which per the comment above is also exactly when a guest count could
    // have changed. Deliberately not depending on `reloadOpenSessions`'s own
    // identity beyond mount: it is stable per `useApi`'s `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableStates]);
  const guestCountByTable = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of rowsOf<{ tableId: string | null; guestCount: number }>(openSessions.data).rows) {
      if (row.tableId) map.set(row.tableId, row.guestCount);
    }
    return map;
  }, [openSessions.data]);

  /**
   * §38 — one list of the real operational entities, tables and resources
   * together, carrying the area and floor they belong to. The editor places
   * elements *from* this list, so a placed element is linked by construction
   * rather than linked as an afterthought.
   */
  const entities = useMemo<PlaceableEntity[]>(
    () => [
      ...tableRows.map<PlaceableEntity>((row) => ({
        id: row.id,
        kind: 'VenueTable',
        label: t('spaces.qr.tableNumber', { number: row.number }),
        areaId: row.area?.id ?? '',
        areaName: row.area?.name ?? t('spaces.resources.noArea'),
        floorId: row.area?.floorId ?? null,
        capacity: row.seats ?? null,
      })),
      ...resourceRows.map<PlaceableEntity>((row) => ({
        id: row.id,
        kind: 'Resource',
        label: row.name,
        areaId: row.area?.id ?? '',
        areaName: row.area?.name ?? t('spaces.resources.noArea'),
        floorId: row.area?.floorId ?? null,
        capacity: row.capacity ?? null,
      })),
    ],
    [resourceRows, t, tableRows],
  );

  const liveFill = useCallback(
    (element: SceneElement) => {
      if (element.entityType === 'VenueTable' && element.entityId) {
        // No default of AVAILABLE. An unconfirmed table is grey, because
        // guessing "free" is the guess that gets a table double-seated.
        const state = tableStates[element.entityId];
        return state
          ? (tableStateColors[state as keyof typeof tableStateColors] ?? colors.neutral[400])
          : colors.neutral[300];
      }
      if (element.entityType === 'Resource' && element.entityId) {
        // A live meter is the more specific truth than the row's availability
        // column, and it is what the resource's own card shows — so the map
        // must agree with it rather than paint a second story.
        const status = liveResourceStatus(
          resourceStates[element.entityId] ?? 'AVAILABLE',
          resources.sessionsByResource.get(element.entityId),
        );
        return (
          resourceStatusColors[status as keyof typeof resourceStatusColors] ?? colors.neutral[400]
        );
      }
      return elementFill[element.elementType];
    },
    [resourceStates, resources.sessionsByResource, tableStates],
  );

  const nameOf = useCallback(
    (element: SceneElement) => {
      if (element.entityId) {
        const table = tableRows.find((row) => row.id === element.entityId);
        if (table) return t('spaces.qr.tableNumber', { number: table.number });
        const resource = resourceRows.find((row) => row.id === element.entityId);
        if (resource) return resource.name;
      }
      return element.label ?? t(`spaces.element.${element.elementType}`);
    },
    [resourceRows, t, tableRows],
  );

  const detailOf = useCallback(
    (element: SceneElement) => {
      if (!element.entityId) return null;
      if (element.entityType === 'VenueTable') {
        const state = tableStates[element.entityId];
        return state ? t(`table.state.${state}`, state) : t('spaces.live.unconfirmedState');
      }
      const status = resourceStates[element.entityId];
      if (!status) return null;
      const live = liveResourceStatus(status, resources.sessionsByResource.get(element.entityId));
      return t(`spaces.resources.status.${live}`, live);
    },
    [resourceStates, resources.sessionsByResource, t, tableStates],
  );

  /**
   * The 3D view's only extra structural read beyond what the 2D board
   * already uses — a resource's own `type` (PLAYSTATION vs BILLIARDS is
   * not on the map element, see `../lib/furniture`'s file header) and a
   * table's seat count. Both come off the same `tableRows`/`resourceRows`
   * the rest of this screen reads, so there is nothing here that could
   * disagree with the 2D board.
   *
   * Deliberately NOT a dependency of `guestCountByTable` — that map changes
   * on every realtime tick (see the comment above it), and `FloorMap3D`'s
   * geometry-build effect depends on this callback directly so it can
   * revisit a resource's shape once the resource list actually finishes
   * loading (see `ElementShapeInfo`'s file header in `../lib/furniture`).
   * If this closed over `guestCountByTable` too, its identity would churn
   * every tick and rebuild the whole scene's geometry every tick with it —
   * exactly the regression that type split exists to prevent.
   */
  const elementShapeFor = useCallback(
    (element: SceneElement): ElementShapeInfo => {
      if (element.entityType === 'VenueTable' && element.entityId) {
        const table = tableRows.find((row) => row.id === element.entityId);
        return { resourceType: null, seats: table?.seats ?? null };
      }
      if (element.entityType === 'Resource' && element.entityId) {
        const resource = resourceRows.find((row) => row.id === element.entityId);
        return { resourceType: resource?.type ?? null, seats: resource?.capacity ?? null };
      }
      // Not `furniture.ts`'s `NO_SHAPE_INFO` constant — see the import
      // comment above on why this file takes no runtime binding from there.
      return { resourceType: null, seats: null };
    },
    [resourceRows, tableRows],
  );

  /**
   * Guests currently seated at a linked table, from `guestCountByTable`
   * above. Live, read every tick by `FloorMap3D`'s reconcile effect —
   * never by its geometry-build effect, see `elementShapeFor` above.
   */
  const guestCountFor = useCallback(
    (element: SceneElement): number | null => {
      if (element.entityType !== 'VenueTable' || !element.entityId) return null;
      return guestCountByTable.get(element.entityId) ?? null;
    },
    [guestCountByTable],
  );

  const onFloorSaved = useCallback((saved: FloorScene) => {
    setFloors((current) =>
      current.map((candidate) => (candidate.id === saved.id ? { ...candidate, ...saved } : candidate)),
    );
  }, []);

  const svgRef = useRef<SVGSVGElement>(null);
  const zoomBy = useCallback(
    (factor: number) =>
      setView((current) => {
        if (!scene) return current;
        const zoom = clamp(current.zoom * factor, 0.3, 5);
        const cx = current.x + scene.width / current.zoom / 2;
        const cy = current.y + scene.height / current.zoom / 2;
        return { zoom, x: cx - scene.width / zoom / 2, y: cy - scene.height / zoom / 2 };
      }),
    [scene],
  );

  const selected = scene?.elements.find((element) => element.id === selection[0]) ?? null;

  /**
   * §5.2 live mode — the selected element resolved back to the operational
   * table it stands for. An unlinked shape (a wall, or a table element nobody
   * ever linked) has no session and no actions, so it stays a plain caption.
   */
  const moveTargets = useMemo<MoveTarget[]>(
    () =>
      tableRows.map((row) => ({
        id: row.id,
        number: row.number,
        seats: row.seats,
        state: tableStates[row.id] ?? UNKNOWN_STATE,
        areaName: row.area?.name ?? t('spaces.resources.noArea'),
      })),
    [t, tableRows, tableStates],
  );

  const tableForElementId = useCallback(
    (entityId: string | null | undefined): LiveTable | null => {
      const row = entityId ? tableRows.find((candidate) => candidate.id === entityId) : undefined;
      if (!row) return null;
      return {
        id: row.id,
        number: row.number,
        // An unconfirmed table gets UNKNOWN, not its last colour: the context
        // menu derives "can be seated" / "can be freed" from this through
        // TABLE_TRANSITIONS, and offering to seat a table on stale state is the
        // exact mistake worth one extra second of waiting.
        state: tableStates[row.id] ?? UNKNOWN_STATE,
        areaName: row.area?.name ?? t('spaces.resources.noArea'),
        branchId: row.area?.floor?.branchId ?? floor?.branchId ?? null,
      };
    },
    [floor?.branchId, t, tableRows, tableStates],
  );

  const selectedTable = useMemo<LiveTable | null>(
    () =>
      selected?.entityType === 'VenueTable' ? tableForElementId(selected.entityId) : null,
    [selected, tableForElementId],
  );

  /**
   * The actions belong on the table, so a click opens them there.
   *
   * A single tap opens it at the pointer. The anchor is the tap itself, which
   * is on the table, so the menu lands where the operator is already looking
   * instead of in a panel further down.
   */
  const openMenuAt = useCallback(
    (event: { clientX: number; clientY: number }, element: SceneElement | undefined) => {
      const anchor = { x: event.clientX, y: event.clientY };
      // §5.2 supports `entityType: 'Resource'`, and the venue draws its PS5
      // room and its kids zone on the same map as its tables — so a tap on one
      // opens its actions exactly as a tap on a table does.
      if (element?.entityType === 'Resource' && element.entityId) {
        const row = resourceRows.find((candidate) => candidate.id === element.entityId);
        if (!row) return false;
        setMenu({ kind: 'resource', resourceId: row.id, anchor });
        return true;
      }
      const table = tableForElementId(element?.entityId);
      if (!table) return false;
      setMenu({ kind: 'table', table, anchor });
      return true;
    },
    [resourceRows, tableForElementId],
  );

  /**
   * The 3D view's tap — the exact same funnel as `onCanvasPointerDown` below
   * takes for the 2D one. A tap resolves to an element id (or `null`, empty
   * floor), the element is looked up in the same `scene`, and `openMenuAt`
   * decides whether it opens anything at all. Two renderers, one gate.
   */
  const onScene3DPointerDown = useCallback(
    (event: { clientX: number; clientY: number }, elementId: string | null) => {
      if (!elementId) {
        setSelection([]);
        setMenu(null);
        return;
      }
      setSelection([elementId]);
      const element = scene?.elements.find((candidate) => candidate.id === elementId);
      if (!openMenuAt(event, element)) setMenu(null);
    },
    [scene, openMenuAt],
  );

  // The menu describes one table in one state; if either moves underneath it,
  // it is describing something that is no longer true.
  useEffect(() => {
    if (!menu) return;
    if (menu.kind === 'resource') {
      // The resource menu reads the row fresh on every render, so it only has
      // to be closed when the row itself is gone.
      if (!resourceRows.some((row) => row.id === menu.resourceId)) setMenu(null);
      return;
    }
    const fresh = tableForElementId(menu.table.id);
    if (!fresh) setMenu(null);
    else if (fresh.state !== menu.table.state) {
      setMenu((current) =>
        current?.kind === 'table' ? { ...current, table: fresh } : current,
      );
    }
  }, [menu, resourceRows, tableForElementId]);

  /** The live session for whichever table the operator is acting on. */
  const actingTable =
    ordering ?? settling ?? viewing ?? moving ?? (menu?.kind === 'table' ? menu.table : null);
  const canReadSession = can('sessions.view');
  /**
   * `tableStates` (via `liveFill`) already told the board this table is
   * AVAILABLE, RESERVED, BLOCKED or OUT_OF_SERVICE — states
   * `TABLE_TRANSITIONS` never lets a session survive on — so asking
   * `/sessions` for one is a round trip for an answer already known. Only a
   * table that genuinely might have one (§ `tableMayHaveSession`) is worth
   * the fetch; a table whose state has not arrived yet (`!isKnownTableState`)
   * is not "free", so it still gets one.
   */
  const actingTableMayHaveSession =
    !!actingTable && (!isKnownTableState(actingTable.state) || tableMayHaveSession(actingTable.state));
  const actingSessions = useApi<unknown>(
    actingTable && canReadSession && actingTableMayHaveSession ? '/sessions' : null,
    actingTable ? { tableId: actingTable.id, status: 'OPEN', limit: 1 } : undefined,
  );
  const actingSession = useMemo(
    () => rowsOf<{ id: string }>(actingSessions.data).rows[0] ?? null,
    [actingSessions.data],
  );

  /**
   * `null` means "not known yet", which is not the same as "no session": the
   * read is in flight, or this operator may not read sessions at all. Reporting
   * it as `false` made the menu hide close/bill actions the server would have
   * taken, and offer "Open session" on a table that already had one. A table
   * whose state rules a session out entirely skips straight to `false` — see
   * `actingTableMayHaveSession` above — since no fetch was even made for it.
   */
  const hasSession = useMemo<boolean | null>(() => {
    if (!actingTable || !canReadSession) return null;
    if (!actingTableMayHaveSession) return false;
    if (actingSessions.error) return null;
    if (actingSessions.loading || actingSessions.data === null) return null;
    return !!actingSession;
  }, [
    actingSession,
    actingSessions.data,
    actingSessions.error,
    actingSessions.loading,
    actingTable,
    actingTableMayHaveSession,
    canReadSession,
  ]);

  const runAction = useCallback(
    async (table: LiveTable, action: TableAction) => {
      setMenu(null);
      try {
        switch (action) {
          case 'addOrder':
            setOrdering(table);
            return;
          case 'openSession':
            await api.post('/sessions', { tableId: table.id });
            toast.success(t('spaces.live.opened'));
            break;
          case 'requestBill':
            await api.post('/service-requests', {
              type: 'REQUEST_BILL',
              tableId: table.id,
              sessionId: actingSession?.id,
              branchId: table.branchId ?? undefined,
            });
            toast.success(t('spaces.live.billRequested'));
            break;
          case 'closeSession': {
            if (!actingSession) return;
            try {
              await api.post(`/sessions/${actingSession.id}/close`, {});
              toast.success(t('spaces.live.closed'));
            } catch (error) {
              // §7 — the server refuses while orders are still in flight, and
              // "close with force=true" is not something an operator can act on
              // from a toast. Open the visit on its products instead, where the
              // in-flight lines can actually be sent or cancelled.
              if (statusOf(error) === 422) {
                setViewing(table);
                toast.error(apiMessage(error, t('operations.errors.actionFailed')));
                return;
              }
              throw error;
            }
            break;
          }
          case 'moveSession':
            setMoving(table);
            return;
          case 'markAvailable':
            await api.patch(`/tables/${table.id}/state`, { state: 'AVAILABLE' });
            toast.success(t('spaces.live.freed'));
            break;
          case 'viewChecks':
            // The bill belongs on the table, like the order menu does. Leaving
            // for the Checks screen made the operator find the same table twice.
            setSettling(table);
            return;
          case 'viewSession':
            setViewing(table);
            return;
        }
        void reloadTables();
        resources.reload();
      } catch (error) {
        toast.error(apiMessage(error, t('operations.errors.actionFailed')));
      }
    },
    [actingSession, reloadTables, resources, t],
  );

  /** The row the open resource menu is describing, read fresh each render. */
  const menuResource = useMemo<ResourceRow | null>(
    () =>
      menu?.kind === 'resource'
        ? (resourceRows.find((row) => row.id === menu.resourceId) ?? null)
        : null,
    [menu, resourceRows],
  );

  /**
   * Which resources somebody actually drew. Everything else is reachable only
   * through the panel — a venue that never drew its PS5 room must not lose
   * access to it, so the panel says how many are in that position.
   */
  const placedResourceIds = useMemo(() => {
    const ids = new Set<string>();
    for (const candidate of floors) {
      for (const element of candidate.mapElements ?? []) {
        if (element.entityType === 'Resource' && element.entityId) ids.add(element.entityId);
      }
    }
    return ids;
  }, [floors]);

  /**
   * I-13 — the branch a resource bills in, taken from the floor its area sits
   * on rather than from whichever branch happens to be selected.
   */
  const branchIdForResource = useCallback(
    (resource: ResourceRow) =>
      floors.find((candidate) => candidate.id === resource.area?.floorId)?.branchId ??
      floor?.branchId ??
      null,
    [floor?.branchId, floors],
  );

  /**
   * §10.1 — one funnel for every resource action, whether it came from the map
   * menu or from a card in the panel, so the two cannot drift.
   */
  const runResourceAction = useCallback(
    async (action: ResourceAction, resource: ResourceRow, session: SessionRow | null) => {
      setMenu(null);
      if (action === 'details') {
        setBoard({ open: true, focusId: resource.id });
        return;
      }
      // C-16 — the resource itself. `edit` opens the form; `retire` asks first,
      // because `DELETE /resources/:id` does not refuse a resource that is in
      // use and the operator should hear that from us, not discover it.
      if (action === 'edit') {
        setResourceForm({ open: true, record: resource });
        return;
      }
      /**
       * §10.2 — "what does this cost?", answered by going to the thing that
       * decides it rather than by opening an editor on the console.
       *
       * Two destinations, and which one is not a preference: a rate lives on a
       * card that ten consoles share, so a console already attached to one goes
       * to the card, while a console attached to nothing goes to its own form —
       * `pricingListId` is a field on the resource and there is nowhere else to
       * set it. Offering the card editor to a console on no card would be a
       * screen that cannot fix the problem it was opened for.
       */
      if (action === 'pricing') {
        const listId = resource.pricingListId ?? resource.pricingList?.id ?? null;
        if (listId) navigate(pricingListRoute(listId));
        else setResourceForm({ open: true, record: resource });
        return;
      }
      if (action === 'retire') {
        // `DELETE /resources/:id` hard-deletes the row. `ResourceSession.resource`
        // cascades in the schema (`prisma/schema.prisma`), so every session ever
        // run on this resource is destroyed with it — only the check/order-item
        // money survives, unlinked (`onDelete: SetNull`). Never a soft "retire",
        // so the confirm below says that plainly and points at the `isActive`
        // toggle as the reversible alternative.
        //
        // Live sessions (ACTIVE/PAUSED) block the delete server-side with a 409
        // that carries no machine-readable `code` — see
        // `resources.service.ts#remove` — so it cannot ride Pricing's
        // code-keyed `refusalSentence` without silently landing on the generic
        // one. Checked here first from data already on screen, so the common
        // case never makes a doomed round trip at all.
        const live = resources.sessionsByResource.get(resource.id) ?? [];
        if (live.length > 0) {
          toast.error(t('spaces.resourceForm.deleteBlockedLiveTitle', { name: resource.name }), {
            description: t('spaces.resourceForm.deleteBlockedLiveBody', { name: resource.name }),
          });
          return;
        }
        const ok = await askConfirm({
          title: t('spaces.resourceForm.deleteTitle', { name: resource.name }),
          body: t('spaces.resourceForm.deleteBody', { name: resource.name }),
          confirmLabel: t('spaces.resourceForm.delete'),
          tone: 'danger' as const,
        });
        if (!ok) return;
        try {
          await api.delete(`/resources/${resource.id}`);
          toast.success(t('spaces.resourceForm.deleted', { name: resource.name }));
          resources.reload();
        } catch (err) {
          // Covers a session that started between the check above and the
          // click — the server is still the last word.
          if (deleteResourceOutcome(statusOf(err)) === 'blockedByLiveSession') {
            toast.error(t('spaces.resourceForm.deleteBlockedLiveTitle', { name: resource.name }), {
              description: t('spaces.resourceForm.deleteBlockedLiveBody', { name: resource.name }),
            });
          } else {
            toast.error(t('spaces.resourceForm.deleteFailedTitle', { name: resource.name }), {
              description: t('spaces.resourceForm.deleteFailedBody'),
            });
          }
        }
        return;
      }
      // Pause and resume have nothing to ask first, so they post straight away.
      if (action === 'pause' || action === 'resume') {
        if (!session) return;
        await resources.act(
          session,
          action,
          action === 'pause' ? 'spaces.resources.paused' : 'spaces.resources.resumed',
        );
        return;
      }
      // §11 — `resourceDialogFor` is what keeps a kids area on its own
      // check-in/check-out flow, which carries the child, the guardian and the
      // wristband; the generic start/stop pair cannot and must not be reached.
      setResourceDialog(resourceDialogFor(action, resource, session));
    },
    [resources, navigate],
  );

  if (!routed.open) return routed.redirect;

  /** One source of truth for who may see which mode: the table the route uses. */
  const offers = (id: FloorMapMode) => routed.tabs.some((entry) => entry.id === id);

  if (mode === 'qr') return <TableQrSheet onBack={() => setMode('live')} />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">{t('nav.floorMap')}</h1>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex overflow-hidden rounded-lg border">
            <button
              type="button"
              onClick={() => setMode('live')}
              aria-pressed={mode === 'live'}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm ${mode === 'live' ? 'bg-accent-solid text-accent-on' : 'hover:bg-hover'}`}
            >
              <Radio className="h-4 w-4" />
              {t('spaces.mode.live')}
            </button>
            {offers('edit') && (
              <button
                type="button"
                onClick={() => setMode('edit')}
                aria-pressed={mode === 'edit'}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm ${mode === 'edit' ? 'bg-accent-solid text-accent-on' : 'hover:bg-hover'}`}
              >
                <Pencil className="h-4 w-4" />
                {t('spaces.mode.edit')}
              </button>
            )}
          </div>

          {/* §10 — the resource floor, folded in. It sits outside the map
              because a resource nobody ever drew is still a resource this
              venue runs, and this is the way in to it. */}
          {can('resources.view') && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBoard({ open: true, focusId: null })}
            >
              <Gamepad2 className="h-4 w-4" />
              {t('nav.resources')}
              {resourceRows.length > 0 && (
                <span className="tabular-nums text-ink-muted">
                  {resourceRows.length}
                </span>
              )}
            </Button>
          )}

          {offers('qr') && (
            <Button variant="outline" size="sm" onClick={() => setMode('qr')}>
              <QrCodeIcon className="h-4 w-4" />
              {t('spaces.mode.qr')}
            </Button>
          )}
        </div>
      </div>

      {floors.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {floors.map((candidate) => (
            <button
              key={candidate.id}
              onClick={() => setActiveFloorId(candidate.id)}
              className={
                candidate.id === activeFloorId
                  ? 'rounded-lg bg-accent-solid px-3 py-1.5 text-sm text-accent-on'
                  : 'rounded-lg border px-3 py-1.5 text-sm hover:bg-hover'
              }
            >
              {candidate.name}
            </button>
          ))}
        </div>
      )}

      {scenes.isLoading ? (
        <LoadingState label={t('common.loading')} />
      ) : scenes.error ? (
        <ErrorState
          message={
            (scenes.error as { message?: string }).message ?? t('operations.errors.actionFailed')
          }
          onRetry={() => void scenes.refetch()}
        />
      ) : !floor || !scene ? (
        <EmptyState title={t('spaces.noFloors')} hint={t('spaces.noFloorsHint')} />
      ) : mode === 'edit' ? (
        <FloorMapEditor
          key={floor.id}
          floor={floor}
          entities={entities}
          canPublish={can('floor_map.publish')}
          onSaved={onFloorSaved}
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
            {Object.entries(tableStateColors).map(([state, color]) => (
              <span key={state} className="flex items-center gap-1.5">
                <span className="h-3 w-3 rounded-sm" style={{ background: color }} />
                {t(`table.state.${state}`, state)}
              </span>
            ))}
            {/* The map says out loud when its colours are not confirmed, rather
                than showing the last ones it happened to have. */}
            {unconfirmed && (
              <Badge tone="warning">
                {live.connected ? t('spaces.live.confirming') : t('spaces.live.offline')}
              </Badge>
            )}
            <span className="ms-auto flex items-center gap-2">
              {/* A toggle, not a replacement — 3D is the default on a
                  device that can render it, but 2D is exactly one click
                  away and the choice is remembered per device
                  (`lib/floorMapView.ts`). Both read the same `scene`, the
                  same `liveFill`, the same tap-to-menu path. */}
              <div className="flex overflow-hidden rounded-lg border">
                <button
                  type="button"
                  onClick={() => {
                    setViewMode('2d');
                    storeFloorMapView('2d');
                  }}
                  aria-pressed={viewMode === '2d'}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm ${viewMode === '2d' ? 'bg-accent-solid text-accent-on' : 'hover:bg-hover'}`}
                >
                  <LayoutGrid className="h-4 w-4" />
                  {t('spaces.live.view2d')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setViewMode('3d');
                    storeFloorMapView('3d');
                  }}
                  aria-pressed={viewMode === '3d'}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm ${viewMode === '3d' ? 'bg-accent-solid text-accent-on' : 'hover:bg-hover'}`}
                >
                  <Box className="h-4 w-4" />
                  {t('spaces.live.view3d')}
                </button>
              </div>
              {viewMode === '2d' && (
                <span className="flex items-center gap-1">
                  <Button size="sm" variant="outline" onClick={() => zoomBy(1 / 1.2)} title={t('spaces.editor.zoomOut')}>
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <span className="w-12 text-center tabular-nums">{Math.round(view.zoom * 100)}%</span>
                  <Button size="sm" variant="outline" onClick={() => zoomBy(1.2)} title={t('spaces.editor.zoomIn')}>
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setView({ x: 0, y: 0, zoom: 1 })}
                    title={t('spaces.editor.zoomReset')}
                  >
                    <Maximize className="h-4 w-4" />
                  </Button>
                </span>
              )}
            </span>
          </div>

          <Card>
            <CardContent className="p-4">
              {scene.elements.length === 0 ? (
                <EmptyState
                  title={t('spaces.emptyScene')}
                  hint={t('spaces.emptySceneHint')}
                  action={
                    can('floor_map.update') ? (
                      <Button size="sm" onClick={() => setMode('edit')}>
                        <Pencil className="h-4 w-4" />
                        {t('spaces.mode.edit')}
                      </Button>
                    ) : undefined
                  }
                />
              ) : viewMode === '3d' ? (
                <div
                  className="w-full overflow-hidden rounded-lg"
                  style={{
                    aspectRatio: `${scene.width} / ${scene.height}`,
                    minHeight: 360,
                    maxHeight: '70vh',
                  }}
                >
                  <Suspense fallback={<LoadingState label={t('common.loading')} />}>
                    <FloorMap3D
                      scene={scene}
                      grid={40}
                      fillFor={liveFill}
                      labelFor={nameOf}
                      selection={selection}
                      elementShapeFor={elementShapeFor}
                      guestCountFor={guestCountFor}
                      onElementPointerDown={onScene3DPointerDown}
                      reducedMotion={reducedMotion}
                      ariaLabel={t('spaces.live.view3dCanvasLabel', { floor: scene.name })}
                      className="h-full w-full"
                    />
                  </Suspense>
                </div>
              ) : (
                <MapCanvas
                  ref={svgRef}
                  scene={scene}
                  mode="live"
                  view={view}
                  grid={40}
                  showGrid
                  selection={selection}
                  fillFor={liveFill}
                  labelFor={nameOf}
                  detailFor={detailOf}
                  onCanvasPointerDown={(event, target) => {
                    if (target.kind !== 'element') {
                      setSelection([]);
                      setMenu(null);
                      return;
                    }
                    setSelection([target.id]);
                    const element = scene.elements.find((candidate) => candidate.id === target.id);
                    // A linked table or resource gets its actions under the
                    // pointer; a wall or an unlinked shape has none, so it just
                    // selects.
                    if (!openMenuAt(event, element)) setMenu(null);
                  }}
                  // The dashboard runs on tills and tablets, so a tap is the
                  // only way in — there is no right-click on a touchscreen. This
                  // only suppresses the browser's own menu, which a long-press
                  // would otherwise pop on top of ours.
                  onCanvasContextMenu={(event) => event.preventDefault()}
                  ariaLabel={t('spaces.liveCanvasLabel', { floor: scene.name })}
                  className="w-full"
                />
              )}
            </CardContent>
          </Card>

          {/* A click on a linked table is the manager's way into the work
              (§5.2): state, live session, open check total and the actions
              their permissions allow. Anything else stays a caption. */}
          {menu?.kind === 'table' && (
            <TableContextMenu
              table={menu.table}
              anchor={menu.anchor}
              hasSession={hasSession}
              canReadSession={canReadSession}
              onPick={(action) => void runAction(menu.table, action)}
              onClose={() => setMenu(null)}
            />
          )}

          {/* §10 — the same tap, on a PS5, a billiards table or a kids zone. */}
          {menu?.kind === 'resource' && menuResource && (
            <ResourceContextMenu
              resource={menuResource}
              sessions={resources.sessionsByResource.get(menuResource.id) ?? []}
              anchor={menu.anchor}
              canReadSessions={resources.canReadSessions}
              pending={resources.canReadSessions && resources.sessionsLoading}
              currency={menuResource.currency ?? currencyCode}
              onPick={(action, session) => void runResourceAction(action, menuResource, session)}
              onClose={() => setMenu(null)}
            />
          )}

          {moving && (
            <MoveSessionDialog
              from={moving}
              sessionId={actingSession?.id ?? null}
              tables={moveTargets}
              open
              onClose={() => setMoving(null)}
              onMoved={() => {
                void reloadTables();
                resources.reload();
              }}
            />
          )}

          {(viewing || settling) && (
            <TableSessionDialog
              table={(viewing ?? settling) as LiveTable}
              sessionId={actingSession?.id ?? null}
              open
              initialPane={settling ? 'bill' : 'products'}
              onClose={() => {
                setViewing(null);
                setSettling(null);
              }}
              onChanged={() => {
                void reloadTables();
                resources.reload();
              }}
            />
          )}


          {ordering && (
            <QuickOrderDialog
              target={{
                kind: 'table',
                table: ordering,
                sessionId: actingSession?.id ?? null,
              }}
              open
              onClose={() => setOrdering(null)}
              onOrdered={() => {
                void reloadTables();
                resources.reload();
              }}
            />
          )}

          {/* No panel under the map: the table's own menu carries every action
              and every detail, so a second copy below would only drift. A shape
              that is not a table still gets a caption, because there is nothing
              to open on a wall. */}
          {!selectedTable && selected ? (
            <Card>
              <CardContent className="flex flex-wrap items-center gap-3 p-4 text-sm">
                <span className="font-medium">{nameOf(selected)}</span>
                <Badge tone="neutral">{t(`spaces.element.${selected.elementType}`)}</Badge>
                {detailOf(selected) && <Badge tone="info">{detailOf(selected)}</Badge>}
                {!selected.entityId && (
                  <span className="text-xs text-ink-muted">
                    {t('spaces.live.notLinked')}
                  </span>
                )}
              </CardContent>
            </Card>
          ) : (
            <p className="text-xs text-ink-muted">{t('spaces.live.selectHint')}</p>
          )}
        </>
      )}

      {/* Rendered outside the map branches on purpose: with no floor drawn at
          all, the panel is the only way to reach a resource, and it still has
          to work. The dialogs come after it so one opened from a card in the
          panel draws on top of it. */}
      <ResourceBoard
        open={board.open}
        board={resources}
        focusResourceId={board.focusId}
        placedResourceIds={placedResourceIds}
        onAction={(action, resource, session) =>
          void runResourceAction(action, resource, session)
        }
        onNew={() => setResourceForm({ open: true, record: null })}
        onClose={() => setBoard({ open: false, focusId: null })}
      />

      <ResourceFormDialog
        open={resourceForm.open}
        record={resourceForm.record}
        // Whether this resource's own icon is actually drawn on a floor right
        // now — the area picker only warns about a move stranding the map
        // element when there is one to strand.
        isPlaced={resourceForm.record ? placedResourceIds.has(resourceForm.record.id) : false}
        onClose={() => setResourceForm({ open: false, record: null })}
        onSaved={resources.reload}
        onDelete={(resource) => {
          setResourceForm({ open: false, record: null });
          void runResourceAction('retire', resource, null);
        }}
      />

      <ResourceDialogs
        dialog={resourceDialog}
        sessionsByResource={resources.sessionsByResource}
        branchIdFor={branchIdForResource}
        onClose={() => setResourceDialog(null)}
        onDone={resources.reload}
      />
    </div>
  );
}
